import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Cpu, Search, X } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
const fmtNum = (n) => (typeof n === "number" ? new Intl.NumberFormat("fr-FR").format(n) : "—");
const fmtGbps = (g) => {
  if (typeof g !== "number") return "—";
  if (g >= 1) return `${g.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Gbps`;
  return `${Math.round(g * 1000)} Mbps`;
};

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [specs, setSpecs] = useState([]);
  const [specsOpen, setSpecsOpen] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => setItems(r.data.items));
    api.get("/fortigate/specs").then((r) => setSpecs(r.data.items || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);

  // Catalog has 24 000+ SKUs — never render the full list. Require either a
  // search term (≥2 chars) or an explicit category filter to surface rows.
  const queryActive = q.trim().length >= 2 || cat !== "All";
  const MAX_RESULTS = 100;
  const matches = useMemo(() => {
    if (!queryActive) return [];
    const needle = q.trim().toLowerCase();
    const rows = items.filter((i) => {
      if (cat !== "All" && i.category !== cat) return false;
      if (needle && !`${i.sku} ${i.name} ${i.description}`.toLowerCase().includes(needle)) return false;
      return true;
    });
    return rows;
  }, [items, q, cat, queryActive]);
  const filtered = matches.slice(0, MAX_RESULTS);
  const overflow = matches.length - filtered.length;

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 mb-6 sm:mb-8">Catalogue produits</h1>

      {/* FortiGate hardware specifications comparator */}
      <div className="mb-6 border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="catalog-fg-specs">
        <button
          type="button"
          onClick={() => setSpecsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
          data-testid="catalog-fg-specs-toggle"
        >
          <div className="flex items-center gap-3">
            <Cpu className="h-5 w-5 text-[#D42B1E]" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Comparatif FortiGate — spécifications matériel</p>
              <p className="text-xs text-zinc-500">
                Source : Fortinet Product Comparison · Threat Protection · NGFW · SSL Inspection · Sessions concurrentes · IPSec VPN
              </p>
            </div>
          </div>
          {specsOpen ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
        </button>
        {specsOpen && specs.length > 0 && (
          <div className="border-t border-zinc-200 overflow-x-auto" data-testid="catalog-fg-specs-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 sticky left-0 bg-zinc-50 z-10">Modèle</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Threat&nbsp;Protection</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">NGFW</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">IPS</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">SSL&nbsp;Inspection</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Sessions concurrentes</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Nouvelles sessions/s</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">IPSec VPN</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">VDOMs</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Interfaces</th>
                </tr>
              </thead>
              <tbody>
                {specs.map((s, idx) => (
                  <tr key={s.sku} className={`border-b border-zinc-100 ${idx % 2 ? "bg-zinc-50/40" : ""}`} data-testid={`catalog-fg-spec-row-${s.sku}`}>
                    <td className="px-3 py-2.5 sticky left-0 bg-inherit z-10">
                      <div className="font-semibold text-zinc-900">{s.model_name}</div>
                      <div className="font-mono text-[10px] text-zinc-400">{s.sku} · {s.form_factor}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-900 whitespace-nowrap">{fmtGbps(s.threat_protection_throughput_gbps)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{fmtGbps(s.ngfw_throughput_gbps)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{fmtGbps(s.ips_throughput_gbps)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{fmtGbps(s.ssl_inspection_throughput_gbps)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-900 whitespace-nowrap">{fmtNum(s.concurrent_sessions)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{fmtNum(s.new_sessions_per_sec)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{fmtGbps(s.ipsec_vpn_throughput_gbps)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">{s.max_vdoms}</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-600 max-w-[280px]">{s.interfaces}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Search / category filter — primary UX. The full 24 000+ SKU list is
          only displayed AFTER the partner types something or picks a category,
          to avoid scrolling fatigue and keep the page snappy. */}
      <div className="mb-6 border border-zinc-200 bg-white rounded-sm" data-testid="catalog-search-card">
        <div className="px-5 py-5 sm:py-6">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-[#D42B1E] shrink-0" />
            <Input
              autoFocus
              placeholder="Rechercher par référence, nom de produit ou mot-clé (ex. FG-100F, FortiAnalyzer, BDL-950, SD-WAN)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0 text-base placeholder:text-zinc-400 px-0 h-11"
              data-testid="catalog-search-input"
            />
            {q && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQ("")}
                className="rounded-sm h-8 px-2 text-zinc-500"
                data-testid="catalog-search-clear"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`catalog-filter-${c}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${
                  cat === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
                }`}
              >
                {c === "All" ? "Tout" : c}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-zinc-400" data-testid="catalog-results-meta">
            Catalogue : <span className="font-mono text-zinc-700">{items.length.toLocaleString("fr-FR")}</span> références au total
            {queryActive ? (
              <> · <span className="font-mono text-zinc-700">{matches.length.toLocaleString("fr-FR")}</span> correspondance(s){overflow > 0 ? <> (affichage des {MAX_RESULTS} premiers)</> : null}</>
            ) : null}
          </p>
        </div>
      </div>

      {!queryActive ? (
        <div className="border border-dashed border-zinc-200 bg-white rounded-sm p-10 sm:p-16 text-center" data-testid="catalog-empty-state">
          <Search className="h-10 w-10 text-zinc-300 mx-auto" />
          <p className="mt-3 text-sm font-medium text-zinc-700">Tapez votre recherche pour explorer le catalogue</p>
          <p className="mt-1 text-xs text-zinc-500 max-w-md mx-auto">
            Le catalogue contient {items.length.toLocaleString("fr-FR")} références — la liste reste masquée jusqu'à ce que vous saisissiez au moins 2 caractères ou choisissiez une catégorie ci-dessus. Cela garde la page fluide et la recherche instantanée.
          </p>
        </div>
      ) : (
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="catalog-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Référence</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Produit</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Description</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Catégorie</th>
                <th className="px-4 py-2.5 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Prix public</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i, idx) => (
                <tr key={i.sku + idx} className={`border-b border-zinc-100 hover:bg-zinc-50 ${idx % 2 ? "bg-zinc-50/40" : ""}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{i.sku}</td>
                  <td className="px-4 py-2.5 text-zinc-900 font-medium">{i.name}</td>
                  <td className="px-4 py-2.5 text-zinc-600 text-xs max-w-xl">{i.description}</td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs">{i.category}</td>
                  <td className="px-4 py-2.5 font-mono text-right text-zinc-900">{fmt(i.price)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">Aucun résultat pour « {q} ».</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {overflow > 0 && (
          <div className="px-4 py-3 border-t border-zinc-100 text-xs text-zinc-500 bg-zinc-50/60" data-testid="catalog-overflow-notice">
            {overflow.toLocaleString("fr-FR")} résultat(s) supplémentaire(s) — affinez votre recherche pour les voir.
          </div>
        )}
      </div>
      )}
    </div>
  );
}
